import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EventCard from './components/EventCard';
import EventFilters from './components/EventFilters';
import CalendarView from './components/CalendarView';
import RegisteredEvents from './components/RegisteredEvents';
import ExpertProfile from './components/ExpertProfile';
import EventDetailsModal from './components/EventDetailsModal';

const EventsAndWorkshops = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentView, setCurrentView] = useState('grid');
  const [activeTab, setActiveTab] = useState('browse');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [showExpertProfile, setShowExpertProfile] = useState(false);
  
  const [filters, setFilters] = useState({
    type: 'all',
    category: 'all',
    tier: 'all',
    dateRange: 'all',
    startDate: '',
    endDate: '',
    searchQuery: '',
    resultCount: 0
  });

  // Mock user data
  const user = {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    tier: "All Access",
    joinDate: "2024-01-15",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face"
  };

  // Mock notifications
  const notifications = [
    {
      id: 1,
      title: "Workshop Reminder",
      message: "Mindfulness Meditation starts in 1 hour",
      time: "1 hour ago",
      read: false
    },
    {
      id: 2,
      title: "New Event Available",
      message: "Advanced Nutrition Workshop now open for registration",
      time: "3 hours ago",
      read: false
    }
  ];

  // Mock events data
  const mockEvents = [
    {
      id: 1,
      title: "Mindfulness Meditation for Beginners",
      description: "Learn the fundamentals of mindfulness meditation and develop a daily practice that reduces stress and improves mental clarity.",
      fullDescription: `Join us for an immersive introduction to mindfulness meditation. This workshop is designed for complete beginners who want to understand the principles of mindfulness and develop a sustainable daily practice.\n\nYou'll learn various meditation techniques, understand the science behind mindfulness, and receive practical tools to integrate meditation into your busy lifestyle. Our expert instructor will guide you through breathing exercises, body awareness techniques, and mindful movement practices.`,
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      type: "Virtual",
      category: "mindfulness",
      tierRequirement: "Intro",
      dateTime: "2024-10-15T10:00:00",
      duration: "2 hours",
      price: 0,
      capacity: 50,
      registered: 32,
      status: "Available",
      featured: true,
      expert: {
        id: 1,
        name: "Dr. Emily Chen",
        credentials: "PhD in Psychology, Certified Mindfulness Instructor",
        profileImage: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face",
        bio: `Dr. Emily Chen is a licensed psychologist with over 15 years of experience in mindfulness-based interventions. She holds a PhD in Clinical Psychology from Stanford University and is a certified instructor through the Mindfulness-Based Stress Reduction (MBSR) program.\n\nDr. Chen has worked with thousands of individuals, helping them develop sustainable mindfulness practices that enhance their overall well-being. Her approach combines traditional meditation techniques with modern psychological insights.`,
        specializations: ["Mindfulness Meditation", "Stress Reduction", "Anxiety Management", "Sleep Improvement"],
        certifications: [
          "Certified MBSR Instructor",
          "Licensed Clinical Psychologist",
          "Mindfulness-Based Cognitive Therapy Certification"
        ],
        rating: 4.9,
        reviewCount: 247,
        totalEvents: 156,
        totalAttendees: 3420,
        yearsExperience: 15,
        isFollowing: false,
        email: "dr.chen@wellnessbank.com",
        website: "https://dremilychenmindfulness.com",
        socialMedia: "dremilychenmindfulness",
        recentReviews: [
          {
            memberName: "Michael R.",
            rating: 5,
            comment: "Dr. Chen\'s guidance was transformative. I finally understand how to meditate properly.",
            date: "2 weeks ago",
            eventTitle: "Advanced Mindfulness Techniques"
          },
          {
            memberName: "Lisa K.",
            rating: 5,
            comment: "Excellent instructor with deep knowledge and patience for beginners.",
            date: "1 month ago",
            eventTitle: "Mindfulness for Stress Relief"
          }
        ]
      },
      learningObjectives: [
        "Understand the fundamentals of mindfulness meditation",
        "Learn 5 different breathing techniques",
        "Develop a personalized daily practice routine",
        "Master stress reduction through mindful awareness"
      ],
      prerequisites: "No prior meditation experience required",
      agenda: [
        {
          time: "10:00 AM",
          title: "Welcome & Introduction",
          description: "Getting to know each other and setting intentions"
        },
        {
          time: "10:15 AM",
          title: "Mindfulness Fundamentals",
          description: "Understanding what mindfulness is and isn\'t"
        },
        {
          time: "10:45 AM",
          title: "Breathing Techniques",
          description: "Learning and practicing various breathing methods"
        },
        {
          time: "11:30 AM",
          title: "Guided Meditation",
          description: "20-minute guided meditation session"
        },
        {
          time: "11:50 AM",
          title: "Q&A and Closing",
          description: "Questions, resources, and next steps"
        }
      ],
      materials: [
        {
          title: "Meditation Guide PDF",
          description: "Comprehensive guide with techniques and tips",
          available: true
        },
        {
          title: "Audio Meditations",
          description: "5 guided meditation recordings",
          available: true
        },
        {
          title: "Practice Journal Template",
          description: "Printable journal for tracking progress",
          available: true
        }
      ],
      hasRecording: true,
      testimonial: {
        text: "This workshop changed my approach to stress management completely!",
        author: "Jennifer M."
      }
    },
    {
      id: 2,
      title: "Advanced Nutrition Workshop",
      description: "Deep dive into nutritional science and learn how to create personalized meal plans for optimal health and wellness.",
      image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=300&fit=crop",
      type: "Local",
      category: "nutrition",
      tierRequirement: "All Access",
      dateTime: "2024-10-18T14:00:00",
      duration: "3 hours",
      price: 45,
      capacity: 25,
      registered: 18,
      status: "Available",
      featured: false,
      expert: {
        id: 2,
        name: "Maria Rodriguez",
        credentials: "RD, MS, Certified Nutritionist",
        profileImage: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=150&h=150&fit=crop&crop=face",
        bio: "Maria Rodriguez is a registered dietitian with a Master's degree in Nutritional Science. She specializes in personalized nutrition plans and has helped over 2,000 clients achieve their health goals through sustainable dietary changes.",
        specializations: ["Personalized Nutrition", "Weight Management", "Sports Nutrition", "Digestive Health"],
        certifications: [
          "Registered Dietitian (RD)","Master of Science in Nutrition","Certified Sports Nutritionist"
        ],
        rating: 4.8,
        reviewCount: 189,
        totalEvents: 98,
        totalAttendees: 2156,
        yearsExperience: 12,
        isFollowing: true,
        email: "maria.rodriguez@wellnessbank.com",website: "https://marianutrition.com",socialMedia: "marianutritionrd",
        recentReviews: [
          {
            memberName: "David L.",rating: 5,comment: "Maria\'s practical approach to nutrition made everything so clear and achievable.",date: "1 week ago",eventTitle: "Meal Planning Masterclass"
          }
        ]
      },
      testimonial: {
        text: "Maria\'s expertise helped me finally understand proper nutrition!",author: "Robert K."
      }
    },
    {
      id: 3,
      title: "HIIT Fitness Bootcamp",description: "High-intensity interval training session designed to boost cardiovascular health and build strength.",image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop",type: "Local",category: "fitness",tierRequirement: "Flex",dateTime: "2024-10-20T09:00:00",duration: "1.5 hours",price: 25,capacity: 30,registered: 30,status: "Full",
      featured: false,
      expert: {
        id: 3,
        name: "Jake Thompson",credentials: "NASM-CPT, Fitness Specialist",profileImage: "https://images.unsplash.com/photo-1567515004624-219c11d31f2e?w=150&h=150&fit=crop&crop=face",bio: "Jake Thompson is a certified personal trainer with expertise in high-intensity training and functional fitness. He has been helping clients achieve their fitness goals for over 8 years.",
        specializations: ["HIIT Training", "Strength Building", "Functional Fitness", "Athletic Performance"],
        certifications: [
          "NASM Certified Personal Trainer","Functional Movement Screen Certified","HIIT Specialist Certification"
        ],
        rating: 4.7,
        reviewCount: 156,
        totalEvents: 87,
        totalAttendees: 1890,
        yearsExperience: 8,
        isFollowing: false,
        email: "jake.thompson@wellnessbank.com",
        recentReviews: []
      }
    },
    {
      id: 4,
      title: "Sleep Optimization Workshop",description: "Learn evidence-based strategies to improve sleep quality and establish healthy sleep habits.",image: "https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?w=400&h=300&fit=crop",type: "Virtual",category: "lifestyle",tierRequirement: "Premium",dateTime: "2024-10-22T19:00:00",duration: "2.5 hours",price: 60,capacity: 40,registered: 15,status: "Available",
      featured: true,
      expert: {
        id: 4,
        name: "Dr. Sarah Williams",credentials: "MD, Sleep Medicine Specialist",profileImage: "https://images.unsplash.com/photo-1594824388853-d0c1e0a3d3e5?w=150&h=150&fit=crop&crop=face",bio: "Dr. Sarah Williams is a board-certified sleep medicine physician with over 10 years of experience helping patients overcome sleep disorders and optimize their sleep quality.",
        specializations: ["Sleep Medicine", "Insomnia Treatment", "Sleep Hygiene", "Circadian Rhythm Disorders"],
        certifications: [
          "Board Certified in Sleep Medicine","Doctor of Medicine (MD)","American Board of Sleep Medicine Diplomate"
        ],
        rating: 4.9,
        reviewCount: 203,
        totalEvents: 67,
        totalAttendees: 1456,
        yearsExperience: 10,
        isFollowing: false,
        email: "dr.williams@wellnessbank.com",
        recentReviews: []
      },
      testimonial: {
        text: "Finally sleeping through the night thanks to Dr. Williams\' strategies!",author: "Amanda T."
      }
    }
  ];

  // Mock registered events
  const mockRegisteredEvents = [
    {
      ...mockEvents?.[0],
      registrationDate: "2024-10-08",
      hasMaterials: true,
      hasRecording: false
    },
    {
      id: 5,
      title: "Stress Management Techniques",
      description: "Learn practical techniques for managing daily stress and building resilience.",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      type: "Virtual",
      category: "mental-health",
      tierRequirement: "Intro",
      dateTime: "2024-09-25T16:00:00",
      duration: "2 hours",
      price: 0,
      capacity: 50,
      registered: 45,
      status: "Completed",
      expert: {
        id: 1,
        name: "Dr. Emily Chen",
        credentials: "PhD in Psychology, Certified Mindfulness Instructor",
        profileImage: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face"
      },
      registrationDate: "2024-09-20",
      hasMaterials: true,
      hasRecording: true
    }
  ];

  // Filter events based on current filters
  const filteredEvents = mockEvents?.filter(event => {
    if (filters?.type !== 'all' && event?.type?.toLowerCase() !== filters?.type) return false;
    if (filters?.category !== 'all' && event?.category !== filters?.category) return false;
    if (filters?.tier !== 'all' && event?.tierRequirement !== filters?.tier) return false;
    if (filters?.searchQuery && !event?.title?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase()) && 
        !event?.description?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase()) &&
        !event?.expert?.name?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase())) return false;
    
    if (filters?.dateRange !== 'all') {
      const eventDate = new Date(event.dateTime);
      const today = new Date();
      
      switch (filters?.dateRange) {
        case 'today':
          if (eventDate?.toDateString() !== today?.toDateString()) return false;
          break;
        case 'week':
          const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
          if (eventDate < today || eventDate > weekFromNow) return false;
          break;
        case 'month':
          const monthFromNow = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
          if (eventDate < today || eventDate > monthFromNow) return false;
          break;
        case 'custom':
          if (filters?.startDate && eventDate < new Date(filters.startDate)) return false;
          if (filters?.endDate && eventDate > new Date(filters.endDate)) return false;
          break;
      }
    }
    
    return true;
  });

  // Update result count when filters change
  useEffect(() => {
    setFilters(prev => ({ ...prev, resultCount: filteredEvents?.length }));
  }, [filteredEvents?.length]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      type: 'all',
      category: 'all',
      tier: 'all',
      dateRange: 'all',
      startDate: '',
      endDate: '',
      searchQuery: '',
      resultCount: filteredEvents?.length
    });
  };

  const handleEventRegister = (event) => {
    console.log('Registering for event:', event?.title);
    // Handle registration logic here
  };

  const handleEventDetails = (event) => {
    setSelectedEvent(event);
    setShowEventDetails(true);
  };

  const handleExpertProfile = (expert) => {
    setSelectedExpert(expert);
    setShowExpertProfile(true);
  };

  const handleCalendarEventClick = (event) => {
    if (event?.events) {
      // Handle multiple events for a day
      console.log('Multiple events for date:', event?.date);
    } else {
      handleEventDetails(event);
    }
  };

  const handleCancelRegistration = (event) => {
    console.log('Cancelling registration for:', event?.title);
    // Handle cancellation logic here
  };

  const handleAccessMaterials = (event) => {
    console.log('Accessing materials for:', event?.title);
    // Handle materials access logic here
  };

  const handleFollowExpert = (expert) => {
    console.log('Following expert:', expert?.name);
    // Handle follow logic here
  };

  const renderEventGrid = () => {
    if (filteredEvents?.length === 0) {
      return (
        <div className="col-span-full flex flex-col items-center justify-center py-12">
          <Icon name="Calendar" size={64} className="text-muted-foreground mb-4" />
          <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
            No Events Found
          </h3>
          <p className="text-muted-foreground text-center mb-4">
            Try adjusting your filters or search terms to find more events.
          </p>
          <Button variant="outline" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </div>
      );
    }

    if (currentView === 'list') {
      return (
        <div className="space-y-4">
          {filteredEvents?.map((event) => (
            <div key={event?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-wellness transition-wellness">
              <div className="flex items-start space-x-4">
                <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={event?.image}
                    alt={event?.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-foreground mb-2">
                        {event?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {event?.description}
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Icon name="Calendar" size={14} />
                          <span>{new Date(event.dateTime)?.toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="Clock" size={14} />
                          <span>{event?.duration}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name={event?.type === 'Virtual' ? 'Video' : 'MapPin'} size={14} />
                          <span>{event?.type}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 ml-4">
                      <div className="text-right">
                        <div className="font-semibold text-foreground">
                          {event?.price > 0 ? `$${event?.price}` : 'Free'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {event?.registered}/{event?.capacity} registered
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEventDetails(event)}
                        >
                          Details
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleEventRegister(event)}
                          disabled={event?.status === 'Full'}
                        >
                          {event?.status === 'Full' ? 'Full' : 'Register'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEvents?.map((event) => (
          <EventCard
            key={event?.id}
            event={event}
            onRegister={handleEventRegister}
            onViewDetails={handleEventDetails}
            userTier={user?.tier}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={user}
        notifications={notifications}
        onNotificationClick={(notification) => console.log('Notification clicked:', notification)}
        onProfileClick={() => console.log('Profile clicked')}
      />
      <div className="flex">
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          user={user}
        />

        <main className={`flex-1 transition-all duration-300 ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        }`}>
          <div className="p-6">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="font-heading font-bold text-3xl text-foreground mb-2">
                    Events & Workshops
                  </h1>
                  <p className="text-muted-foreground">
                    Discover and join wellness events, workshops, and expert-led sessions
                  </p>
                </div>
                
                {/* Quick Stats */}
                <div className="hidden lg:flex items-center space-x-6">
                  <div className="text-center">
                    <div className="font-semibold text-2xl text-primary">{filteredEvents?.length}</div>
                    <div className="text-xs text-muted-foreground">Available Events</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-2xl text-accent">{mockRegisteredEvents?.length}</div>
                    <div className="text-xs text-muted-foreground">Your Events</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-2xl text-success">
                      {mockEvents?.filter(e => e?.tierRequirement === user?.tier || e?.tierRequirement === 'All')?.length}
                    </div>
                    <div className="text-xs text-muted-foreground">Tier Eligible</div>
                  </div>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="flex space-x-1 bg-muted/30 p-1 rounded-lg w-fit">
                <button
                  onClick={() => setActiveTab('browse')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-wellness ${
                    activeTab === 'browse' ?'bg-card text-foreground shadow-wellness' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Browse Events
                </button>
                <button
                  onClick={() => setActiveTab('registered')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-wellness ${
                    activeTab === 'registered' ?'bg-card text-foreground shadow-wellness' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  My Events ({mockRegisteredEvents?.length})
                </button>
              </div>
            </div>

            {/* Content */}
            {activeTab === 'browse' ? (
              <div className="space-y-6">
                {/* Filters */}
                <EventFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                  onViewToggle={setCurrentView}
                  currentView={currentView}
                />

                {/* Events Display */}
                {currentView === 'calendar' ? (
                  <CalendarView
                    events={filteredEvents}
                    onEventClick={handleCalendarEventClick}
                  />
                ) : (
                  renderEventGrid()
                )}
              </div>
            ) : (
              <RegisteredEvents
                events={mockRegisteredEvents}
                onViewEvent={handleEventDetails}
                onCancelRegistration={handleCancelRegistration}
                onAccessMaterials={handleAccessMaterials}
              />
            )}
          </div>
        </main>
      </div>
      {/* Modals */}
      {showEventDetails && selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => {
            setShowEventDetails(false);
            setSelectedEvent(null);
          }}
          onRegister={handleEventRegister}
          onExpertProfile={handleExpertProfile}
          userTier={user?.tier}
          isRegistered={mockRegisteredEvents?.some(e => e?.id === selectedEvent?.id)}
        />
      )}
      {showExpertProfile && selectedExpert && (
        <ExpertProfile
          expert={selectedExpert}
          onClose={() => {
            setShowExpertProfile(false);
            setSelectedExpert(null);
          }}
          onViewEvents={(expert) => {
            setShowExpertProfile(false);
            setSelectedExpert(null);
            setFilters(prev => ({ ...prev, searchQuery: expert?.name }));
          }}
          onFollow={handleFollowExpert}
        />
      )}
    </div>
  );
};

export default EventsAndWorkshops;